import { renderKatexAccessible } from "../../../../../snapcourse/helpers/react/renderKatexAccessible"

/**
 * Applies accessibility attributes to math formulas in the DOM.
 * Handles both Quill editor format (.ql-formula) and pre-rendered KaTeX format (.katex).
 * Uses direct DOM manipulation to work with innerHTML-injected content.
 *
 * @param element - The DOM element to search for math formulas within
 */
export const makeMathAccessible = (element: HTMLElement) => {
  // First pass: Handle pre-rendered KaTeX format (.katex with annotation)
  // Do this BEFORE Quill format to avoid double-wrapping
  element.querySelectorAll(".katex").forEach(katexElement => {
    // Skip if already processed
    const elementRole = katexElement.parentElement?.getAttribute("role")
    if (elementRole === "math" || elementRole === "img") return
    // Skip if inside a .ql-formula (will be handled by Quill handler)
    if (katexElement.closest("[data-math-processed]")) return

    const annotation = katexElement.querySelector(
      "annotation[encoding='application/x-tex']"
    )
    const tex = annotation?.textContent
    if (!tex) return

    const { speakable, isPlainNumber } = renderKatexAccessible(tex, {
      displayMode: false
    })

    if (isPlainNumber) {
      // Replace math with plain text - screen readers handle this perfectly
      const textNode = document.createTextNode(speakable)
      katexElement.parentNode?.replaceChild(textNode, katexElement)
      return
    }

    // Real math expression - add accessibility
    if (!speakable) return

    const wrapper = createMathWrapper(speakable)
    katexElement.setAttribute("aria-hidden", "true")
    katexElement.parentNode?.insertBefore(wrapper, katexElement)
    wrapper.appendChild(katexElement)
  })

  // Second pass: Handle Quill editor format (.ql-formula[data-value])
  element.querySelectorAll(".ql-formula[data-value]").forEach(mathElement => {
    const tex = mathElement.getAttribute("data-value")
    if (!tex) return

    const {
      html: katexHtml,
      speakable,
      isPlainNumber
    } = renderKatexAccessible(tex, { displayMode: false })

    if (isPlainNumber) {
      // Replace math with plain text - screen readers handle this perfectly
      const textNode = document.createTextNode(speakable)
      mathElement.parentNode?.replaceChild(textNode, mathElement)
      return
    }

    // Real math expression - render and add accessibility
    if (!speakable) return

    const wrapper = createMathWrapper(
      speakable,
      katexHtml,
      (mathElement as HTMLElement).style.backgroundColor
    )
    // Mark as processed so KaTeX elements inside don't get wrapped again
    wrapper.setAttribute("data-math-processed", "true")
    mathElement.parentNode?.replaceChild(wrapper, mathElement)
  })
}

/**
 * Creates a wrapper span with math accessibility attributes.
 * @param speakableText - The human-readable text for screen readers
 * @param visualHtml - Optional HTML to render inside (if not provided, caller appends children)
 * @param backgroundColor - Optional background color from original element
 */
function createMathWrapper(
  speakableText: string,
  visualHtml?: string,
  backgroundColor?: string
): HTMLSpanElement {
  const wrapper = document.createElement("span")
  wrapper.setAttribute("data-math-processed", "true")
  wrapper.style.display = "inline-block"
  if (backgroundColor) {
    wrapper.style.backgroundColor = backgroundColor
  }

  // Screen reader-only text with the speakable representation
  const srOnly = document.createElement("span")
  srOnly.className = "sr-only"
  srOnly.textContent = speakableText
  wrapper.appendChild(srOnly)

  if (visualHtml) {
    const hiddenSpan = document.createElement("span")
    hiddenSpan.setAttribute("aria-hidden", "true")
    hiddenSpan.innerHTML = visualHtml
    wrapper.appendChild(hiddenSpan)
  }

  return wrapper
}
